import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigationElement {
  id: string;
  onBack: () => void;
}

interface NavigationContextType {
  navigationStack: NavigationElement[];
  pushToStack: (element: NavigationElement) => void;
  popFromStack: () => void;
  updateStack: (id: string, newElement: NavigationElement) => void;
  deleteFromStack: (id: string) => void;
  removeFromStack: (id: string) => void; // Remove without navigation
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [navigationStack, setNavigationStack] = useState<NavigationElement[]>(
    [],
  );
  const navigate = useNavigate();
  const locksRef = useRef(new Set<string>());

  /*
   * The stack is mirrored into a ref so every callback below can stay
   * referentially stable: these functions are dependencies of
   * `useGeneralState`'s setState, which in turn is the `setShowSection` that
   * components key effects on. Re-creating them on each stack change made
   * unrelated effects (e.g. the composer's `textarea.focus()`) re-run on every
   * dropdown/modal open, which dismissed the just-opened Radix menu.
   *
   * The ref is updated eagerly by the mutators (so two calls in the same tick
   * still see each other) and re-synced from state on commit.
   */
  const stackRef = useRef<NavigationElement[]>(navigationStack);
  useEffect(() => {
    stackRef.current = navigationStack;
  }, [navigationStack]);

  /*
   * How many upcoming popstate events are ours (from a programmatic
   * `navigate(-1)`) and must not run an onBack. A ref rather than state: it is
   * pure bookkeeping, never rendered, and as state it re-subscribed the
   * popstate listener on every change.
   */
  const pendingBackNavigations = useRef<number>(0);

  useEffect(() => {
    const handlePopState = () => {
      if (pendingBackNavigations.current > 0) {
        pendingBackNavigations.current -= 1;
        return;
      }
      const stack = stackRef.current;
      if (stack.length === 0) return;
      stack[stack.length - 1].onBack();
      setNavigationStack(currentStack => currentStack.slice(0, -1));
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  /* Walk back `count` history entries, ignoring the popstates they produce. */
  const goBack = useCallback(
    (count: number, onLastStep?: () => void) => {
      if (count <= 0) return;
      pendingBackNavigations.current += count;
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          navigate(-1);
          if (i === count - 1) onLastStep?.();
        }, 50 * i);
      }
    },
    [navigate],
  );

  const pushToStack = useCallback(
    (element: NavigationElement) => {
      const newPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      navigate(newPath, { state: { modal: true } });
      stackRef.current = [...stackRef.current, element];
      setNavigationStack(prevStack => [...prevStack, element]);
    },
    [navigate],
  );

  const popFromStack = useCallback(() => {
    stackRef.current = stackRef.current.slice(0, -1);
    setNavigationStack(prevStack =>
      prevStack.length > 0 ? prevStack.slice(0, -1) : prevStack,
    );
    pendingBackNavigations.current += 1;
    navigate(-1);
  }, [navigate]);

  const updateStack = useCallback(
    (id: string, newElement: NavigationElement) => {
      const replace = (stack: NavigationElement[]) => {
        const index = stack.findIndex(el => el.id === id);
        if (index === -1) return stack;
        const newStack = [...stack];
        newStack[index] = newElement;
        return newStack;
      };
      stackRef.current = replace(stackRef.current);
      setNavigationStack(replace);
    },
    [],
  );

  /*
   * Both removers below decide how many entries go away BEFORE touching state:
   * the counter bump and the `navigate(-1)` calls used to live inside the
   * setNavigationStack updater, and React may invoke an updater more than once
   * (always under StrictMode), which popped a second real history entry per
   * close and could walk the tab clean off the app.
   */
  const deleteFromStack = useCallback(
    (id: string) => {
      if (locksRef.current.has(id)) return;

      const numRemoved = stackRef.current.filter(el => el.id === id).length;
      if (numRemoved === 0) return;

      locksRef.current.add(id);
      stackRef.current = stackRef.current.filter(el => el.id !== id);
      setNavigationStack(currentStack =>
        currentStack.filter(el => el.id !== id),
      );
      goBack(numRemoved, () => locksRef.current.delete(id));
    },
    [goBack],
  );

  // Remove from stack without triggering the element's onBack callback
  // (for programmatic closes).
  const removeFromStack = useCallback(
    (id: string) => {
      const numRemoved = stackRef.current.filter(el => el.id === id).length;
      if (numRemoved === 0) return;

      stackRef.current = stackRef.current.filter(el => el.id !== id);
      setNavigationStack(currentStack =>
        currentStack.filter(el => el.id !== id),
      );
      goBack(numRemoved);
    },
    [goBack],
  );

  const value = useMemo(
    () => ({
      navigationStack,
      pushToStack,
      popFromStack,
      updateStack,
      deleteFromStack,
      removeFromStack,
    }),
    [
      navigationStack,
      pushToStack,
      popFromStack,
      updateStack,
      deleteFromStack,
      removeFromStack,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
